import React, { useEffect, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Button } from '@makemydeal/dr-ui-bricks-components';
import { ApiRequestStatus, CDL_V3_EVENT } from '@makemydeal/dr-shared-types';
import { PageHeader } from '@makemydeal/dr-sales-checkin-components';
import { SCI_CDL_TAGS } from '@makemydeal/dr-sales-checkin-types';

import { useRequestProposalActions } from '../../hook/useRequestProposalActions';
import { useRequestChangeModal } from '../../hook/useRequestChangeModal';
import { useDealSummaryPdf } from '../../hook/useDealSummaryPdf';
import { HeaderRow, ActionBar, ViewQuoteContainer } from './ViewQuote.styles';
import OfferComments from '../OfferComments/OfferComments';
import { PdfViewer } from '../PdfViewer/PdfViewer';
import { getRequestStatus, getPdfLoadingStatus, getDealPdfUrl, requestProposalSelectors } from '../../store';

const ViewQuote: React.FC = () => {
    const dispatch = useDispatch();
    const { handleAcceptDeal } = useRequestProposalActions();
    const { open: handleOpenRequestChangesModal } = useRequestChangeModal();
    useDealSummaryPdf();
    const requestStatus = useSelector(getRequestStatus);
    const isAcceptingDeal = requestStatus === ApiRequestStatus.Loading;

    const isLoading = useSelector(getPdfLoadingStatus);
    const dealPdfUrl = useSelector(getDealPdfUrl);
    const hasSentViewTag = useRef(false);

    const isAmdOriginated = useSelector(requestProposalSelectors.additionalSelectors.isAmdOriginated);
    const dealVisibility = useSelector(requestProposalSelectors.additionalSelectors.getDealVisibility);
    const canAcceptDeal = !isAmdOriginated || !!dealVisibility?.approvedByManager;

    // dc-sci-073: Deal Summary Workflow Display. Assumes SCI-only — re-check if reused in MV.
    // Gated on the PDF URL so a failed fetch isn't a view; ref-guarded against background refreshes.
    useEffect(() => {
        if (hasSentViewTag.current || !dealPdfUrl) return;

        hasSentViewTag.current = true;
        dispatch({ type: CDL_V3_EVENT, payload: SCI_CDL_TAGS['dc-sci-073']() });
    }, [dealPdfUrl, dispatch]);

    const renderActions = (position: 'top' | 'bottom') => (
        <ActionBar $position={position} data-testid={`view-quote-actions-${position}`}>
            <Button
                data-testid={`request-changes-button-${position}`}
                buttonStyle="secondary"
                onClick={handleOpenRequestChangesModal}
                disabled={isLoading}
            >
                Request Changes/Edit Deal
            </Button>
            {canAcceptDeal && (
                <Button
                    data-testid={`accept-deal-button-${position}`}
                    width="160px"
                    buttonStyle="primary"
                    buttonSize={position === 'top' ? 'small' : 'large'}
                    disabled={isAcceptingDeal || isLoading}
                    onClick={handleAcceptDeal}
                >
                    Accept Deal
                </Button>
            )}
        </ActionBar>
    );

    return (
        <ViewQuoteContainer data-testid="view-quote-page">
            <HeaderRow data-testid="view-quote-header">
                <PageHeader heading="Deal Summary" />
                {renderActions('top')}
            </HeaderRow>
            <OfferComments />
            <PdfViewer view="viewQuote" />
            {renderActions('bottom')}
        </ViewQuoteContainer>
    );
};

export default ViewQuote;
